import { Component, computed, inject, OnInit, PLATFORM_ID, signal } from '@angular/core';
import { DOCUMENT, isPlatformServer } from '@angular/common';
import { BannerService } from '../../../services/banner.service';
import { Banner } from '../../models/banner.model';
import { SafehtmlPipe } from '../../../pipes/safehtml.pipe';

const MOBILE_QUERY = '(max-width: 992px)';
const DESKTOP_QUERY = '(min-width: 993px)';
const SWIPE_THRESHOLD = 50;

@Component({
  selector: 'app-banner',
  imports: [SafehtmlPipe],
  templateUrl: './banner.component.html',
  styleUrl: './banner.component.scss',
})
export class BannerComponent implements OnInit {
  private bannerService = inject(BannerService);
  private document = inject(DOCUMENT);
  private platformId = inject(PLATFORM_ID);

  readonly mobileQuery = MOBILE_QUERY;

  banners = signal<Banner[]>([]);
  current = signal(0);
  hasMany = computed(() => this.banners().length > 1);

  private pointerStartX: number | null = null;

  ngOnInit(): void {
    this.getHomeBanners();
  }

  prev(): void {
    const total = this.banners().length;
    this.current.update((i) => (i - 1 + total) % total);
  }

  next(): void {
    const total = this.banners().length;
    this.current.update((i) => (i + 1) % total);
  }

  goTo(index: number): void {
    this.current.set(index);
  }

  onPointerDown(event: PointerEvent): void {
    if (event.pointerType === 'mouse') return;
    this.pointerStartX = event.clientX;
  }

  onPointerUp(event: PointerEvent): void {
    if (this.pointerStartX === null) return;
    const delta = event.clientX - this.pointerStartX;
    this.pointerStartX = null;
    if (!this.hasMany() || Math.abs(delta) < SWIPE_THRESHOLD) return;
    if (delta < 0) {
      this.next();
    } else {
      this.prev();
    }
  }

  private getHomeBanners(): void {
    this.bannerService.getAll().subscribe({
      next: (result) => {
        this.banners.set(result);
        if (result.length > 0 && isPlatformServer(this.platformId)) {
          this.preloadImage(result[0].mobile_background, MOBILE_QUERY);
          this.preloadImage(result[0].background, DESKTOP_QUERY);
        }
      },
      error: (error) => console.error(error),
    });
  }

  // Rendered into the SSR <head> so the browser starts fetching the LCP image
  // for its own viewport before any JS runs.
  private preloadImage(url: string, media: string): void {
    const link = this.document.createElement('link');
    // setAttribute: the server DOM does not reflect `link.as` to the attribute.
    link.setAttribute('rel', 'preload');
    link.setAttribute('as', 'image');
    link.setAttribute('href', url);
    link.setAttribute('media', media);
    link.setAttribute('fetchpriority', 'high');
    this.document.head.appendChild(link);
  }
}
